import { useMemo } from "react";

import { useFeatureFlagFromFirestore } from "./useFeatureFlagFromFirestore";

/**
 * 配信プレイヤー(StreamingV2)の挙動を切り替える feature flag のまとまり。
 * useStreamingFeatureFlags などで解決し、オブジェクトごと引き回す。
 */
export type StreamingFeatureFlags = {
  isLLHLSEnabled: boolean;
  isSignedUrlEnabled: boolean;
  isMMSForVODEnabled: boolean;
  isPollingEnabled: boolean;
  isUseTokenForCast: boolean;
  isMuxDataEnabled: boolean;
};

/**
 * 配信プレイヤーの feature flag 群をまとめて解決する。
 * 未解決のフラグが残っている間は undefined を返す。
 *
 * StreamingV2 は connect() でラップされているため、レンダーごとに新しい
 * オブジェクトを渡すと ownProps の shallow compare が常に不一致になる。
 * memo 化で参照を安定させて不要な再描画を防ぐ。
 */
export const useStreamingFeatureFlags = (
  eventId: string | undefined
): StreamingFeatureFlags | undefined => {
  const isLLHLSEnabled = useFeatureFlagFromFirestore("llhls", eventId, {
    enable: "useLLHLS",
    disable: "disableLLHLS",
  });
  const isSignedUrlEnabled = useFeatureFlagFromFirestore("signedUrl", eventId);
  const isMMSForVODEnabled = useFeatureFlagFromFirestore("MMSForVOD", eventId, {
    enable: "MMSForVOD",
  });
  const isPollingEnabled = useFeatureFlagFromFirestore("polling", eventId, {
    enable: "polling",
    disable: "noPolling",
  });
  const isUseTokenForCast = useFeatureFlagFromFirestore(
    "useTokenForCast",
    eventId,
    { enable: "useTokenForCast", disable: "noUseTokenForCast" }
  );
  const isMuxDataEnabled = useFeatureFlagFromFirestore("muxData", eventId, {
    enable: "enableMux",
    disable: "disableMux",
  });

  return useMemo(() => {
    if (
      isLLHLSEnabled === undefined ||
      isSignedUrlEnabled === undefined ||
      isMMSForVODEnabled === undefined ||
      isPollingEnabled === undefined ||
      isUseTokenForCast === undefined ||
      isMuxDataEnabled === undefined
    ) {
      return undefined;
    }
    return {
      isLLHLSEnabled,
      isSignedUrlEnabled,
      isMMSForVODEnabled,
      isPollingEnabled,
      isUseTokenForCast,
      isMuxDataEnabled,
    };
  }, [
    isLLHLSEnabled,
    isSignedUrlEnabled,
    isMMSForVODEnabled,
    isPollingEnabled,
    isUseTokenForCast,
    isMuxDataEnabled,
  ]);
};

/**
 * CREW の配信ページ用。polling と muxData 以外は未検証のため CREW では使用しない。
 * 未解決のフラグが残っている間は undefined を返す。
 */
export const useCrewStreamingFeatureFlags = (
  eventId: string | undefined
): StreamingFeatureFlags | undefined => {
  const isPollingEnabled = useFeatureFlagFromFirestore("polling", eventId, {
    enable: "polling",
    disable: "noPolling",
  });
  const isMuxDataEnabled = useFeatureFlagFromFirestore("muxData", eventId, {
    enable: "enableMux",
    disable: "disableMux",
  });

  return useMemo(() => {
    if (isPollingEnabled === undefined || isMuxDataEnabled === undefined) {
      return undefined;
    }
    return {
      isLLHLSEnabled: false,
      isSignedUrlEnabled: false,
      isMMSForVODEnabled: false,
      isUseTokenForCast: false,
      isPollingEnabled,
      isMuxDataEnabled,
    };
  }, [isPollingEnabled, isMuxDataEnabled]);
};
